import React, { useEffect, useRef } from 'react'
import styled, { withTheme } from 'styled-components'
import { style } from 'styled-system'
import { themeGet } from '@styled-system/theme-get'
import { Box, Flex, Hide, Button, getPaletteColor } from 'pcln-design-system'
import { ArrowLeft } from 'pcln-icons'
import {
  restoreScrollPosition,
  scrollToTop
} from '../utils/popover-scroll-helper'

interface RootProps extends PopoverProps {
  zIndex: number
  top: number
  color: string
}

const Root = styled(Box)`
  right: 0;
  bottom: 0;
  left: 0;
  ${(props: RootProps) =>
    props.open
      ? {
          position: 'fixed',
          perspective: '600px',
          zIndex: props.zIndex || 0,
          top: props.top || 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: getPaletteColor(
            `${props.color}`,
            'background.lightest'
          ),

          [themeGet('mediaQueries.1')(props)]: {
            position: 'relative',
            top: 'auto',
            display: 'block'
          }
        }
      : null};
`

interface PopoverProps extends PopoverContainerProps {
  color: string
  title: string
  width: number
  zIndex: number
  borderRadius: string
}

export function Popover({
  open = false,
  onDismiss,
  domSelectorForVisuallyContainedElement = '',
  ...props
}: Partial<PopoverProps>) {
  const root = useRef({})
  const mounted = useRef({}) // Used to implement componentDidUpdate logic in hooks

  useEffect(() => {
    const handleDismiss = (event: MouseEvent | KeyboardEvent) => {
      if (
        !open ||
        !root.current ||
        (root.current as any).contains(event.target)
      )
        return
      if (
        domSelectorForVisuallyContainedElement &&
        typeof (event.target as HTMLElement).closest === 'function' &&
        (event.target as HTMLElement).closest(
          domSelectorForVisuallyContainedElement
        )
      ) {
        return
      }
      if (onDismiss) {
        onDismiss(event)
      }
    }
    document.addEventListener('click', handleDismiss)
    return () => {
      document.removeEventListener('click', handleDismiss)
    }
  }, [open, domSelectorForVisuallyContainedElement, onDismiss])

  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true
    }
  }, [])

  useEffect(() => {
    // The useEffect hook here mimics the componentDidUpdate life-cycle hook
    // The scrollY position stored can be used in the return function and passed to restoreScrollPosition
    // In this way we can avoid storing the scrollY in any state in the Popover
    const scrollYPosition = window.scrollY
    if (mounted.current && open === true) {
      scrollToTop()
    }

    return () => {
      if (!mounted.current) return
      if (open === true) {
        restoreScrollPosition(scrollYPosition)
      }
    }
  }, [open])

  return (
    <Root
      ref={(ref: React.RefObject<HTMLElement>) => {
        root.current = ref
      }}
      open={open}
      {...props}
    />
  )
}

const left = style({
  prop: 'left',
  cssProperty: 'left'
})

const right = style({
  prop: 'right',
  cssProperty: 'right'
})

/*
On mounting, the calendar needs to scroll to the selected dates
This will be addressed in v2 date picker (desktop), but mobile (unconfirmed)
*/
const calendarHeight = (props: { openState: boolean; isCalendar: boolean }) =>
  props.isCalendar
    ? {
        height: '100%'
      }
    : null

Popover.Body = styled(Box)`
  transform-origin: top center;
  &.fade-enter {
    opacity: 0;
    transform: rotateX(-45deg);
    transition: all 200ms linear;
  }
  &.fade-enter-active {
    opacity: 1;
    transform: rotateX(0);
  }
  &.fade-exit {
    opacity: 1;
    transform: rotateX(0);
  }
  &.fade-exit-active {
    opacity: 0;
    transform: rotateX(-45deg);
    transition: all 200ms linear;
  }

  flex: 1 1 auto;
  height: ${props => (props.openState ? 'calc(100vh - 64px)' : 0)};
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  ${left} ${right} ${themeGet('mediaQueries.1')} {
    position: absolute;
    overflow: visible;
    height: auto;
  }

  /* hack for calendar scroll issue */
  & > div {
    ${calendarHeight};
  }
`

interface PopoverContainerProps {
  open?: boolean
  onDismiss?: (event: MouseEvent | KeyboardEvent) => void
  position?: string
  leftPos?: number
  rightPos?: number
  children?: React.ReactNode | React.ReactNode[]
  popoverButtonProps?: Record<string, unknown>
  ['data-calendar']?: boolean
  theme: Record<string, unknown>
  domSelectorForVisuallyContainedElement?: string
}

// Simpler API
export function PopoverRootContainer({
  open = false,
  'data-calendar': isCalendar = false,
  onDismiss,
  children,
  position = 'left',
  leftPos = 0,
  rightPos = 0,
  popoverButtonProps = {},
  ...props
}: PopoverContainerProps) {
  const [top, body] = React.Children.toArray(children)
  return (
    <Popover
      {...props}
      open={open}
      onDismiss={onDismiss}
      width={1}
      {...(open ? { zIndex: 2 } : {})}
    >
      <Box
        p={open ? [2, null, 0] : 0}
        color="background.lightest"
        borderRadius="lg"
      >
        <Flex alignItems="center">
          {open && (
            <Hide md lg xl xxl pr={2}>
              <Button
                size="large"
                data-testid="popover-back-button"
                onClick={(event: MouseEvent) => {
                  event.preventDefault()
                  if (onDismiss) {
                    onDismiss(event)
                  }
                }}
                {...popoverButtonProps}
              >
                <ArrowLeft />
              </Button>
            </Hide>
          )}
          <Box width={1}>{top}</Box>
        </Flex>
      </Box>
      {open && (
        <Popover.Body
          color={isCalendar ? null : 'background.lightest'}
          openState={open}
          isCalendar={isCalendar}
          right={position === 'right' ? `${rightPos}px` : null}
          left={position === 'left' ? `${leftPos}px` : null}
          mt={[0, null, isCalendar ? 2 : 0]}
          mr={[0, 0, -3, 0]}
          width={[null, null, isCalendar ? null : 1]}
        >
          {body}
        </Popover.Body>
      )}
    </Popover>
  )
}

export const PopoverContainer = withTheme(PopoverRootContainer)
